import {
  Alert,
  Button,
  Card,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../sections/@dashboard/user';
import AddUserForm from '../sections/@dashboard/user/AddUserForm';
import useUsers from '../sections/@dashboard/user/user';
import { Chip } from '@mui/material';

export default function UserAdmin() {
  const { users, loading, error, refetch } = useUsers();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isAddUserFormOpen, setIsAddUserFormOpen] = useState(false);
  const [isEditUserFormOpen, setIsEditUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // State for handling delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [usersToDelete, setUsersToDelete] = useState([]);

  useEffect(() => {
    if (error) {
      console.error(error);
      setSnackbarMessage("Error loading users!");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  }, [error]);

  // Get the current user's role (assuming this is fetched from a context, API call, or localStorage)
  const userRole = 'admin'; // Replace with the actual logic to get the role of the current user
  const isAdmin = userRole === 'admin'; // Check if the user is an admin
  
  console.log("Current user role:", userRole);

  const TABLE_HEAD = [
    { id: 'name', label: 'Name', align: 'left' },
    { id: 'email', label: 'Email', align: 'left' },
    { id: 'role', label: 'Role', align: 'left' },
    { id: 'company', label: 'Company', align: 'left' },
    { id: 'status', label: 'Status', align: 'left' },
    { id: 'invited', label: 'Invited', align: 'center' }
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users
        .filter(user => user.role !== 'admin') // Filter out admin users
        .map((user) => user.id); // Use ID instead of name for selection
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, userId) => {
    const selectedIndex = selected.indexOf(userId);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, userId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const applySortFilter = (array, comparator, query) => {
    const filteredArray = query
      ? array.filter((user) => user.firstName.toLowerCase().includes(query.toLowerCase()))
      : array;
    return filteredArray.sort(comparator);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => (a[orderBy] < b[orderBy] ? 1 : -1)
      : (a, b) => (a[orderBy] < b[orderBy] ? -1 : 1);
  };

  const filteredUsers = applySortFilter(
    Array.isArray(users) ? users : [],
    getComparator(order, orderBy),
    filterName
  );

  const usersToDisplay = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handle opening the Add User Form
  const handleOpenAddUserForm = () => {
    setIsAddUserFormOpen(true); 
  };

  const handleCloseAddUserForm = () => {
    setIsAddUserFormOpen(false); 
  };

  const handleOpenEditUserForm = (user) => {
    setEditingUser({
      ...user,
      originalStatus: user.status, // Add originalStatus to track the initial status
    });
    setIsEditUserFormOpen(true);
  };
  
  const handleCloseEditUserForm = () => {
    setEditingUser(null);
    setIsEditUserFormOpen(false);
  };

  const handleDeleteUsers = async () => {
    try {
      console.log("Deleting users with IDs:", usersToDelete);  // Log the user IDs to be deleted
  
      // Loop through selected users and delete them
      for (const userId of usersToDelete) {
        const url = `/user/${userId}`;
        console.log("Sending DELETE request to:", url); // Log the URL to ensure it's correct
  
        const response = await fetch(url, {
          method: 'DELETE', // HTTP method for deleting a resource
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',  // Include credentials (cookies or token) if needed for authorization
        });
  
        // Log the response status and text for debugging
        if (response.ok) {
          const responseData = await response.json(); // Read response if it's in JSON format
          console.log("Delete response:", responseData);
        } else {
          const errorText = await response.text();
          console.error("Failed to delete user. Response:", response.status, errorText);
          throw new Error(`Error: ${response.status} - ${errorText}`);
        }
      }
  
      setSnackbarMessage('Selected users deleted successfully!');
      setSnackbarSeverity('success');
      refetch(); // Re-fetch the updated users list
    } catch (error) {
      console.error('Error deleting users:', error.message);
      setSnackbarMessage(error.message || 'Error deleting users!');
      setSnackbarSeverity('error');
    } finally {
      setOpenSnackbar(true);
      setOpenDeleteDialog(false); // Close the delete confirmation dialog
    }
  };

  const handleSaveUser = async () => {
    try {
      const userData = { ...editingUser };
  
      // Remove sensitive fields or fields that shouldn't be updated (e.g., password or status)
      delete userData.password; // if password field exists
      delete userData.status; // explicitly prevent status from being updated
  
      const url = `/user/${editingUser?.id}`;
  
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
        credentials: 'include',
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error: ${response.status} - ${errorText}`);
      }
  
      setSnackbarMessage('User updated successfully!');
      setSnackbarSeverity('success');
      refetch();
      handleCloseEditUserForm();
    } catch (error) {
      console.error('Error saving user:', error.message);
      setSnackbarMessage(error.message || 'Error saving user!');
      setSnackbarSeverity('error');
    } finally {
      setOpenSnackbar(true);
    }
  };
  
  // Update this logic to set `usersToDelete` when delete is triggered
const handleDeleteClick = () => {
  if (selected.length === 0) {
    // Show alert or simply return if no users are selected
    setSnackbarMessage("No users selected for deletion.");
    setSnackbarSeverity("warning");
    setOpenSnackbar(true);
    return;
  }

  setUsersToDelete(selected); // Set selected users to delete
  setOpenDeleteDialog(true); // Open the delete confirmation dialog
};

  // Handle approving user
  const handleApproveUser = async (userId) => {
    try {
      const response = await fetch(`/user/${userId}/approve`, {
        method: 'PATCH', 
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbarMessage(data.message || 'User approved successfully!');
        setSnackbarSeverity('success');
        refetch();
      } else {
        setSnackbarMessage(data.message || 'Error approving user!');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      console.error('Error approving user:', error);
      setSnackbarMessage('Error approving user!');
      setSnackbarSeverity('error');
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <Page title="Users">
      <Container maxWidth = {false} maxHeight = {500}>
  {isAdmin ? (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={15}
      >
        <Typography variant="h4" gutterBottom>
          User Administration
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpenAddUserForm}
          startIcon={<Iconify icon="eva:plus-fill" />}
        >
          Add New User
        </Button>
      </Stack>

      {/* Add User Form Dialog */}
      <AddUserForm open={isAddUserFormOpen} onClose={handleCloseAddUserForm} />

      <Card>
        <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table>
            <UserListHead
              headLabel={TABLE_HEAD}
              order={order}
              orderBy={orderBy}
              rowCount={users.length}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {usersToDisplay.map((user) => {
                const { id, firstName, lastName, emailAddress, role, company, status, isInvited } = user;
                const selectedUser = selected.indexOf(id) !== -1;
                return (
                  <TableRow hover key={id} selected={selectedUser}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUser}
                        onClick={(event) => handleClick(event, id)}
                      />
                    </TableCell>
                    <TableCell>{firstName} {lastName}</TableCell>
                    <TableCell>{emailAddress}</TableCell>
                    <TableCell>{role}</TableCell>
                    <TableCell>{company}</TableCell>
                    <TableCell>{status}</TableCell>
                     <TableCell>
                      {isInvited ? (
                          <Chip label="Invited" color="info" size="small" />
                              ) : (
                             status || 'Active'
                                )}
                    </TableCell>
                    <TableCell align="right">
                      <UserMoreMenu
                        userId={id}
                        onEdit={() => handleOpenEditUserForm(user)} 
                        onDelete={() => {
                          setUsersToDelete([id]);
                          setOpenDeleteDialog(true);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Edit User Form Dialog */}
      <Dialog open={isEditUserFormOpen} onClose={handleCloseEditUserForm}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            variant="outlined"
            fullWidth
            value={editingUser?.firstName || ''}
            onChange={(e) =>
              setEditingUser({ ...editingUser, firstName: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            label="Last Name"
            variant="outlined"
            fullWidth
            value={editingUser?.lastName || ''}
            onChange={(e) =>
              setEditingUser({ ...editingUser, lastName: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email Address"
            variant="outlined"
            fullWidth
            value={editingUser?.emailAddress || ''}
            onChange={(e) =>
              setEditingUser({ ...editingUser, emailAddress: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            label="Company"
            variant="outlined"
            fullWidth
            value={editingUser?.company || ''}
            onChange={(e) =>
              setEditingUser({ ...editingUser, company: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={editingUser?.role || ''}
              onChange={(e) =>
                setEditingUser({ ...editingUser, role: e.target.value })
              }
              sx={{ mb: 2 }}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="guest">Invited Guest</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditUserForm} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveUser} color="primary">
            Save
          </Button>
          <Button onClick={() => handleApproveUser(editingUser?.id)} color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>
          Are you sure you want to delete {usersToDelete.length} user(s)?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteUsers} color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move the Delete Button to the Bottom */}
      <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteClick}
          startIcon={<Iconify icon="eva:trash-2-outline" />}
          disabled={selected.length === 0}
        >
          Delete Selected
        </Button>
      </Stack>
    </>
  ) : (
    <Typography variant="h6" color="error" align="center" mt={5}>
      You do not have permission to view this page.
    </Typography>
  )}

  {/* Snackbar for notifications */}
  <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
    <Alert severity={snackbarSeverity} onClose={() => setOpenSnackbar(false)}>
      {snackbarMessage}
    </Alert>
  </Snackbar>
</Container>
    </Page>
  );
}
